import {
  useNavigate,
  useParams,
  createFileRoute,
  Outlet,
  redirect,
  Link,
} from '@tanstack/react-router';
import { DepartmentTree } from '@/components/Departments/DepartmentTree';
import { ReactNode, useCallback, useMemo } from 'react';
import { getDefaultView } from '@/utils/viewHelpers';
import { useTenantID } from '@/hooks/useTenantID';
import { SelectedDepartment } from '@/types/selectedDepartment';
import { IconEdit, IconEye } from '@/icons';
import {
  getAccessibleDepartmentIds,
  getWriteAccessDepartmentIds,
} from '@/helpers/departmentPermissions';
import {
  checkUserPermissions,
  isTenantProfile,
} from '@/components/Auth/checkUserPermissions';
import { useGetCurrentDepartmentTree } from '@/api/trafikportalen';
import { FormattedMessage } from 'react-intl';
import { useAuth } from '@/stores/auth';

export const Route = createFileRoute('/$tenantId/_layout/departments/_layout')({
  component: DepartmentsLayout,
  beforeLoad: ({
    params: { tenantId, departmentId, view },
  }: {
    params: {
      tenantId: string;
      departmentId: string;
      view: string;
    };
  }) => {
    if (isTenantProfile()) {
      throw redirect({ to: '/$tenantId', params: { tenantId } });
    }
    if (!view) {
      throw redirect({
        to: '/$tenantId/departments/$departmentId/$view',
        params: {
          tenantId,
          departmentId,
          view: getDefaultView('department'),
        },
      });
    }
  },
});

function DepartmentsLayout() {
  const navigate = useNavigate();
  const tenantId = useTenantID();
  const { departmentId } = useParams({ strict: false }) as {
    departmentId?: string;
  };

  const { data: departmentTree } = useGetCurrentDepartmentTree();
  const departmentAccess = useAuth((state) => state.departmentAccess);

  const onlyHasRootDepartment = useMemo(() => {
    if (!departmentTree) return false;

    return departmentTree?.children.length === 0;
  }, [departmentTree]);

  const onViewChange = useCallback(
    (department: SelectedDepartment) => {
      navigate({
        to: '/$tenantId/departments/$departmentId/$view',
        params: {
          tenantId,
          departmentId: department.id,
          view: getDefaultView('department'),
        },
        replace: false,
      });
    },
    [navigate, tenantId],
  );

  const icons = useMemo(() => {
    if (checkUserPermissions({ permission: 'organization:editor' })) {
      return {} as Record<string, ReactNode>;
    }

    const icons = getWriteAccessDepartmentIds().reduce(
      (acc, departmentId) => {
        acc[departmentId] = <IconEdit />;
        return acc;
      },
      {} as Record<string, ReactNode>,
    );

    getAccessibleDepartmentIds().forEach((departmentId) => {
      if (icons[departmentId]) {
        return;
      }

      icons[departmentId] = <IconEye />;
    });

    return icons;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentAccess]);

  return (
    <div className="flex size-full flex-row">
      <aside className="relative hidden size-full flex-col border-background-300 md:flex md:w-panel-md md:border-r-2 lg:h-full lg:w-panel-md xl:w-panel-lg 2xl:w-panel-xl">
        <h2 className="border-b-2 border-background-300 px-4 py-3 text-lg font-semibold">
          <FormattedMessage defaultMessage="Organisation" />
        </h2>
        <DepartmentTree
          selectedDepartmentId={departmentId}
          mobileView={false}
          onChange={onViewChange}
          enabledDepartments={getAccessibleDepartmentIds()}
          icons={icons}
        />
        {onlyHasRootDepartment && departmentId && (
          <Link
            to="/$tenantId/departments/$departmentId/$view"
            params={{ tenantId, departmentId, view: 'advanced' }}
            className="m-4 w-fit whitespace-pre-wrap border p-4 hover:font-bold"
          >
            <FormattedMessage
              defaultMessage={`Önskas avdelningar?\nLägg till en underavdelning i fliken "Avancerat"`}
            />
          </Link>
        )}
      </aside>
      <div className="flex h-full flex-1 flex-col overflow-auto md:h-screen">
        <Outlet />
      </div>
    </div>
  );
}
